import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getServiceProfile } from "@/lib/serviceProfiles";

/*
  QB-02C — Recommendation Preview (SBC-001 §3.5).

  A READ-ONLY drawer that shows the service-profile recommendation
  content BEFORE the consultant chooses to apply it. Opening this
  component must never mutate the quote state.

  Section order (SBC-001 §3.5.2):
    1. Service Summary
    2. Scope
    3. Document Review
    4. Operational Review           (not present in current profiles)
    5. Deliverables
    6. Evidence Expectations
    7. Inclusions
    8. Exclusions
    9. Referrals
    10. Internal Consultant Notes

  Hidden sections show a professional empty-state message. The "Apply
  Recommendations" action is the only writable escape hatch from this
  drawer — clicking it delegates to the parent's overwrite-aware apply
  handler (which will show the confirmation dialog when the consultant
  has edited any protected fields).
*/

function splitBullets(value) {
  if (!value) return [];
  const s = String(value);
  if (s.includes("•")) {
    return s.split(/\n?\s*•\s*/).map((x) => x.trim()).filter(Boolean);
  }
  return s.split("\n").map((x) => x.trim()).filter(Boolean);
}

function PreviewBlock({ heading, value, testid }) {
  const items = splitBullets(value);
  const isEmpty = items.length === 0;
  return (
    <div className="border-t border-border pt-3 first:border-t-0 first:pt-0" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{heading}</p>
      {isEmpty ? (
        <p className="mt-1 text-sm italic text-muted-foreground">
          No recommendation content is available for this section.
        </p>
      ) : items.length === 1 && value && !String(value).includes("•") ? (
        <p className="mt-1 whitespace-pre-line text-sm text-foreground/90">{value}</p>
      ) : (
        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-foreground/90">
          {items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function RecommendationPreviewDialog({
  open,
  onOpenChange,
  serviceCode,
  onApply
}) {
  const profile = getServiceProfile(serviceCode);
  const hasProfile = Boolean(profile);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="qb-recommendation-preview"
        className="rounded-sm sm:max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Recommendation preview</DialogTitle>
          <DialogDescription className="text-xs leading-relaxed text-muted-foreground">
            Review the recommended commercial content before applying it. Opening this preview does not change your quote.
            Consultant-edited content is preserved by default when you apply recommendations.
          </DialogDescription>
        </DialogHeader>

        {!hasProfile ? (
          <div className="rounded-sm border border-border bg-muted/40 p-4 text-sm italic text-muted-foreground" data-testid="recommendation-preview-empty">
            Select a service to view available recommendations.
          </div>
        ) : (
          <div className="space-y-3">
            {/* 1. Service Summary */}
            <div className="rounded-sm border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-foreground/90" data-testid="recommendation-preview-service-summary">
              <p className="eyebrow text-emerald-900">Service summary · {profile.label}</p>
              <dl className="mt-2 grid grid-cols-1 gap-1 md:grid-cols-2">
                {profile.recommendedPricingModel && (
                  <div className="flex gap-2">
                    <dt className="min-w-[9rem] text-muted-foreground">Pricing model</dt>
                    <dd>{profile.recommendedPricingModel}</dd>
                  </div>
                )}
                {profile.typicalDuration && (
                  <div className="flex gap-2">
                    <dt className="min-w-[9rem] text-muted-foreground">Typical duration</dt>
                    <dd>{profile.typicalDuration}</dd>
                  </div>
                )}
                {profile.typicalEffortHours && (
                  <div className="flex gap-2">
                    <dt className="min-w-[9rem] text-muted-foreground">Typical effort</dt>
                    <dd>{profile.typicalEffortHours}</dd>
                  </div>
                )}
                {profile.suggestedFeePresentation && (
                  <div className="flex gap-2">
                    <dt className="min-w-[9rem] text-muted-foreground">Fee presentation</dt>
                    <dd>{profile.suggestedFeePresentation}</dd>
                  </div>
                )}
              </dl>
              <p className="mt-2 text-[11px] italic text-muted-foreground">
                Guidance only. All figures, wording and calculations are decided by the consultant.
              </p>
            </div>

            {/* 2. Scope */}
            <PreviewBlock heading="Scope" value={profile.assessmentScope} testid="recommendation-preview-scope" />

            {/* 3. Document Review */}
            <PreviewBlock heading="Document review" value={profile.documentReviewScope} testid="recommendation-preview-document-review" />

            {/* 4. Operational Review — profiles do not currently carry a
                 dedicated operational-review field; empty state shown. */}
            <PreviewBlock heading="Operational review" value="" testid="recommendation-preview-operational-review" />

            {/* 5. Deliverables */}
            <PreviewBlock heading="Deliverables" value={profile.deliverables} testid="recommendation-preview-deliverables" />

            {/* 6. Evidence Expectations */}
            <PreviewBlock heading="Evidence expectations" value={profile.evidenceRequiredFromProvider} testid="recommendation-preview-evidence" />

            {/* 7. Inclusions */}
            <PreviewBlock heading="Inclusions" value={profile.inclusions} testid="recommendation-preview-inclusions" />

            {/* 8. Exclusions */}
            <PreviewBlock heading="Exclusions" value={profile.exclusions} testid="recommendation-preview-exclusions" />

            {/* 9. Referrals */}
            <PreviewBlock heading="Referrals" value={profile.additionalProfessionalReferrals} testid="recommendation-preview-referrals" />

            {/* 10. Internal Consultant Notes */}
            <PreviewBlock heading="Internal consultant notes" value={profile.internalCommercialNotes} testid="recommendation-preview-internal-notes" />
          </div>
        )}

        <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
            data-testid="qb-recommendation-preview-close"
          >
            Close preview
          </Button>
          {hasProfile && (
            <Button
              type="button"
              onClick={() => onApply?.()}
              className="rounded-sm"
              data-testid="qb-recommendation-preview-apply"
            >
              Apply recommendations
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
